import {
  CallProviderType,
  CallStatus,
  IGroupCallChanged,
  IIncomingCall,
} from '@gem/call/Model/callModel.types';

import { mapDeviceStatusResponse } from 'APP/model/userSettings/userSettingsModel.mapper';
import {
  IPushNotification,
  PushNotificationType,
} from 'APP/store/PushNotifications/PushNotifications.types';
import { ChatState } from 'APP/wsApi/wsAppApi/types/chat/chat_update_model_pb';
import {
  CallIncomingRealtimeEvent,
  PushNotificationRealtimeEvent,
  PushNotificationRealtimeEvent_Type,
  CallUpdate_CallStatus,
  CallUpdate,
  DeviceStatusUpdateEvent,
} from 'APP/wsApi/wsAppApi/types/realtime_event/realtime_event_pb';

import { IDeviceStatusUpdateEvent, IGroupCounterState } from '../counter/counterModel.types';

export function mapIGroupCallChanged(data: CallUpdate): IGroupCallChanged {
  return {
    groupId: data.chatId.toString(),
    channelId: data.channelId.toString(),
    status: mapCallStatus(data.status),
    callProvider: CallProviderType.Livekit,
  };
}

export function mapIIncomingCall(data: CallIncomingRealtimeEvent): IIncomingCall {
  return {
    groupId: data.chatId.toString(),
    channelId: data.channelId.toString(),
    callVisibleDuration: Number(data.callVisibleDuration),
    callerId: data.callerId.toString(),
    callerName: data.callerName,
    callerAvatarUrl: data.callerAvatarUrl || '',
    callProvider: CallProviderType.Livekit,
  };
}

export function mapIGroupCounterState(state: ChatState): IGroupCounterState {
  return {
    groupId: state.chatId.toString(),
    updatedTs: state.updatedTs.toString(),
  };
}

export function mapIPushNotification(event: PushNotificationRealtimeEvent): IPushNotification {
  return {
    pushType: mapPushNotificationType(event.type),
    user: event.userId.toString(),
    group: event.chatId?.toString(),
    // TODO: check if undefined is correct
    groupType: undefined,
    messageId: event.messageId?.toString(),
    // TODO: check if undefined is correct
    messageDatetime: undefined,
    title: event.title,
    subTitle: event.subTitle,
    body: event.body,
    icon: event.icon,
    timestamp: event.notificationTs?.toString() || '',
    badge: 0, //TODO: resolve it
    event: '',
    tag: generatePushNotificationTag(event),
  };
}

export function mapCallStatus(status: CallUpdate_CallStatus): CallStatus {
  if (status === CallUpdate_CallStatus.IN_PROGRESS) {
    return CallStatus.Started;
  }
  if (status === CallUpdate_CallStatus.FINISHED) {
    return CallStatus.Finished;
  }
  //TODO: figure out with statuses
  return CallStatus.Initiated;
}

export function mapPushNotificationType(
  type: PushNotificationRealtimeEvent_Type
): PushNotificationType {
  if (type === PushNotificationRealtimeEvent_Type.CHAT_RENAMED) {
    return PushNotificationType.GroupRenamed;
  }
  if (type === PushNotificationRealtimeEvent_Type.CHAT_AVATAR_CHANGED) {
    return PushNotificationType.GroupAvatarChanged;
  }
  if (type === PushNotificationRealtimeEvent_Type.CHAT_TYPE_CHANGED) {
    return PushNotificationType.GroupTypeChanged;
  }
  if (type === PushNotificationRealtimeEvent_Type.CHAT_CATEGORY_CHANGED) {
    return PushNotificationType.GroupCategoryChanged;
  }
  if (type === PushNotificationRealtimeEvent_Type.CHAT_DELETED) {
    return PushNotificationType.GroupDeleted;
  }
  if (type === PushNotificationRealtimeEvent_Type.CHAT_USER_JOIN) {
    return PushNotificationType.GroupUserJoin;
  }
  if (type === PushNotificationRealtimeEvent_Type.CHAT_USER_LEAVE) {
    return PushNotificationType.GroupUserLeave;
  }
  if (type === PushNotificationRealtimeEvent_Type.CHAT_ADMIN_ADDED) {
    return PushNotificationType.GroupAdminAdded;
  }
  if (type === PushNotificationRealtimeEvent_Type.CHAT_ADMINS_ADDED) {
    return PushNotificationType.GroupAdminsAdded;
  }
  if (type === PushNotificationRealtimeEvent_Type.CHAT_OWNER_CHANGED) {
    return PushNotificationType.GroupOwnerChanged;
  }
  if (type === PushNotificationRealtimeEvent_Type.CHAT_ADMIN_REMOVED) {
    return 'GROUP_ADMIN_REMOVED' as PushNotificationType;
  }
  if (type === PushNotificationRealtimeEvent_Type.CHAT_ADMINS_REMOVED) {
    return PushNotificationType.GroupAdminsAdded;
  }
  if (type === PushNotificationRealtimeEvent_Type.CHAT_USER_REMOVED) {
    return PushNotificationType.GroupUserRemoved;
  }
  if (type === PushNotificationRealtimeEvent_Type.CHAT_USERS_REMOVED) {
    return PushNotificationType.GroupUsersRemoved;
  }
  if (type === PushNotificationRealtimeEvent_Type.CHAT_USER_ADDED) {
    return PushNotificationType.GroupUserAdded;
  }
  if (type === PushNotificationRealtimeEvent_Type.CHAT_USERS_ADDED) {
    return 'GROUP_USERS_ADDED' as PushNotificationType;
  }
  if (type === PushNotificationRealtimeEvent_Type.MESSAGE_NEW) {
    return PushNotificationType.MessageNew;
  }
  if (type === PushNotificationRealtimeEvent_Type.MESSAGE_USER_MENTIONED) {
    return PushNotificationType.MessageUserMentioned;
  }
  if (type === PushNotificationRealtimeEvent_Type.USER_REPLIED_ON_MESSAGE) {
    return PushNotificationType.UserRepliedOnMessage;
  }
  if (type === PushNotificationRealtimeEvent_Type.NEW_THREAD_MESSAGE) {
    return PushNotificationType.NewThreadMessage;
  }
  if (type === PushNotificationRealtimeEvent_Type.NEW_COMMENT_ON_MESSAGE) {
    return PushNotificationType.NewCommentOnMessage;
  }
  if (type === PushNotificationRealtimeEvent_Type.USER_REACTED_ON_MESSAGE) {
    return PushNotificationType.UserReactedOnMessage;
  }
  if (type === PushNotificationRealtimeEvent_Type.MESSAGE_FORWARD) {
    return PushNotificationType.MessageForward;
  }
  if (type === PushNotificationRealtimeEvent_Type.MESSAGE_PIN) {
    return PushNotificationType.MessagePin;
  }
  if (type === PushNotificationRealtimeEvent_Type.MESSAGE_CONFIRM) {
    return PushNotificationType.MessageConfirm;
  }
  if (type === PushNotificationRealtimeEvent_Type.MESSAGE_DELETE) {
    return PushNotificationType.MessageDelete;
  }

  return '' as PushNotificationType;
}

function generatePushNotificationTag(event: PushNotificationRealtimeEvent): string {
  return [event.type, event.chatId, event.messageId, event.notificationTs]
    .filter(Boolean)
    .join('-');
}

export function mapIDeviceStatusUpdate(data: DeviceStatusUpdateEvent): IDeviceStatusUpdateEvent {
  return {
    deviceId: data.deviceId,
    userId: data.userId.toString(),
    status: mapDeviceStatusResponse(data.status),
  };
}
